
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import ApplicationsFurnitureImage from '../../assets/images/ApplicationsFurniture.jpg';
import ApplicationsAutomotiveImage from '../../assets/images/ApplicationsAutomotive.jpg';
import ApplicationsConstructionImage from '../../assets/images/ApplicationsConstruction.jpg';
import ApplicationsAgricultureImage from '../../assets/images/ApplicationsAgriculture.jpg';
import ApplicationsPackagingImage from '../../assets/images/ApplicationsPackaging.jpg';
import ApplicationsHealthcareImage from '../../assets/images/ApplicationsHealthcare.jpg';

const ApplicationsPage = () => {
  const applications = [
    {
      title: 'Furniture & Home Textiles',
      icon: 'ri-sofa-line',
      color: 'bg-blue-100 text-blue-600',
      products: ['Upholstery backing', 'Mattress felt', 'Sofa pad', 'Cushion support layers'],
      description: 'Non-woven materials provide excellent breathability, softness, and durability for home comfort.',
      image: ApplicationsFurnitureImage
    },
    {
      title: 'Automotive Industry',
      icon: 'ri-car-line',
      color: 'bg-green-100 text-green-600',
      products: ['Headliners', 'Trunk liners', 'Interior insulation', 'Seat padding'],
      description: 'Lightweight and sound-absorbing fabrics enhance comfort and performance in vehicles.',
      image: ApplicationsAutomotiveImage
    },
    {
      title: 'Construction & Building',
      icon: 'ri-building-line',
      color: 'bg-orange-100 text-orange-600',
      products: ['Geotextiles', 'Roofing membranes', 'Insulation layers', 'Vapor-barrier layers'],
      description: 'Non-wovens provide strength, stability, and protection against moisture and erosion.',
      image: ApplicationsConstructionImage
    },
    {
      title: 'Agriculture & Horticulture',
      icon: 'ri-plant-line',
      color: 'bg-green-100 text-green-600',
      products: ['Crop covers', 'Weed control fabrics', 'Plant protection wraps', 'Greenhouse materials'],
      description: 'Promote healthy growth while reducing pesticide and water usage for sustainable farming.',
      image: ApplicationsAgricultureImage
    },
    {
      title: 'Packaging & Consumer Goods',
      icon: 'ri-shopping-bag-line',
      color: 'bg-purple-100 text-purple-600',
      products: ['Reusable shopping bags', 'Protective wrapping', 'Disposable packaging', 'Eco-friendly solutions'],
      description: 'Strong, customizable, and environmentally responsible packaging solutions.',
      image: ApplicationsPackagingImage
    },
    {
      title: 'Healthcare & Hygiene',
      icon: 'ri-heart-pulse-line',
      color: 'bg-red-100 text-red-600',
      products: ['Surgical gowns', 'Medical masks', 'Filtration layers', 'Disposable medical supplies'],
      description: 'Safe, sterile, and skin-friendly materials for critical healthcare applications.',
      image: ApplicationsHealthcareImage
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-teal-600 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Versatile Non-Woven Solutions for Every Industry
          </h1>
          <p className="text-xl md:text-2xl text-teal-100 max-w-4xl mx-auto leading-relaxed">
            <strong>Non-woven fabrics from Taishun Global</strong> are engineered to perform across a wide range of industries. 
            Their strength, light weight, and adaptability make them ideal for countless applications—helping our 
            clients innovate and improve efficiency in every field.
          </p>
        </div>
      </section>

      {/* Applications Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {applications.map((app, index) => (
              <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center mb-6">
                    <div className={`w-16 h-16 ${app.color} rounded-lg flex items-center justify-center mr-4`}>
                      <i className={`${app.icon} text-2xl`}></i>
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900">{app.title}</h2>
                  </div>
                  
                  <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                    {app.description}
                  </p>
                  
                  <div className="mb-8">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Applications:</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {app.products.map((product, productIndex) => (
                        <div key={productIndex} className="flex items-center space-x-3">
                          <div className="w-2 h-2 bg-teal-600 rounded-full"></div>
                          <span className="text-gray-700 font-medium">{product}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                  <img
                    src={app.image}
                    alt={`${app.title} applications`}
                    className="rounded-lg shadow-lg object-cover w-full h-80"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Why Choose Taishun Non-Woven Materials?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our materials deliver consistent performance across all applications with 
              <strong> superior quality and environmental responsibility</strong>.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: 'ri-shield-check-line',
                title: 'Superior Durability',
                description: 'Exceptional tensile strength and tear resistance for long-lasting performance'
              },
              {
                icon: 'ri-leaf-line',
                title: 'Eco-Friendly',
                description: 'Sustainable production with recyclable materials and minimal environmental impact'
              },
              {
                icon: 'ri-settings-3-line',
                title: 'Customizable',
                description: 'Tailored solutions to meet specific industry requirements and applications'
              },
              {
                icon: 'ri-global-line',
                title: 'Global Standards',
                description: 'Certified quality that meets international standards and regulations'
              }
            ].map((benefit, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-lg hover:shadow-lg transition-shadow duration-200">
                <div className="w-16 h-16 bg-teal-100 text-teal-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <i className={`${benefit.icon} text-2xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing Section */}
      <section className="py-20 bg-teal-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Innovate with Non-Woven Solutions?
          </h2>
          <p className="text-xl text-teal-100 mb-8 max-w-3xl mx-auto">
            Whether it's comfort, protection, or performance—<strong>Taishun Global delivers reliable 
            non-woven solutions</strong> built for the future of every industry.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/product-characteristics"
              className="bg-white text-teal-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Learn More
            </Link>
            <Link
              to="/contact"
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-teal-600 px-8 py-3 rounded-lg font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Contact Us for Custom Applications
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ApplicationsPage;
